/**
 * Scroll reveal: content fades up as it scrolls into view, staggered by its position among its siblings
 * (the first heading/card appears first, the next a beat later, and so on).
 *
 * How it works
 * - Walks each page section and picks "units": headings, paragraphs, media, buttons, forms, cards
 *   (li / article / class names containing card, tile, item, member, stat, review, point), and
 *   horizontally scrolling rows (carousels) as a whole. Containers are walked into, not animated.
 * - Units start hidden (opacity + `translate`, so hover transforms are untouched) and are revealed once
 *   by an IntersectionObserver. After revealing, the reveal class is removed so the element's own styles
 *   and hover transitions apply again.
 * - Delay = the unit's rank among units of the same parent revealed in the same moment (max 5 steps).
 * - Skipped: the header and footer, the Featured Deals carousel (it has its own quick rise-in), hero banners, the pinned scroll timeline, cart and product pages'
 *   interactive areas, the intro screen, reduced-motion users, and the theme editor's design mode.
 * Without JavaScript nothing is ever hidden.
 */
(() => {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  const SKIP = [
    '.hero', '.page-hero', '.about-hero', '.brand-hero', '.intro',
    'scroll-steps', 'deal-carousel', 'cart-page', 'product-page', '.product-tabs',
    '[data-no-reveal]', '.visually-hidden', '[aria-hidden="true"]', 'script', 'style', 'template', 'noscript', 'svg',
  ].join(',');
  const LEAF = 'h1,h2,h3,h4,h5,h6,p,figure,img,picture,video,blockquote,table,form,details,dl,a.button-pill,button,hr';
  const CARD = /(^|[-_])(card|tile|item|member|stat|review|point|step|slide|brand)([-_]|$)/;

  const units = [];
  const isCard = (el) => el.tagName === 'LI' || el.tagName === 'ARTICLE' || [...el.classList].some((c) => CARD.test(c));
  const scrollsX = (el) => {
    const o = getComputedStyle(el).overflowX;
    return (o === 'auto' || o === 'scroll') && el.scrollWidth > el.clientWidth + 1;
  };

  function collect(root) {
    for (const el of root.children) {
      if (el.matches(SKIP) || el.hidden) continue;
      const pos = getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') continue;
      if (el.matches(LEAF) || isCard(el) || scrollsX(el) || el.tagName.includes('-')) {
        // Custom elements (carousels, counters) animate as a whole unless they hold a scrolling track.
        if (el.tagName.includes('-') && !isCard(el) && !el.matches(LEAF) && !scrollsX(el)) {
          collect(el);
          continue;
        }
        units.push(el);
      } else if (el.children.length) {
        collect(el);
      } else if (el.textContent.trim()) {
        units.push(el);
      }
    }
  }

  document.querySelectorAll('.shopify-section').forEach((section) => {
    if (section.closest('header, footer') || section.matches('[class*="header-group"], [class*="footer-group"]')) return;
    if (section.querySelector(':scope > .hero, :scope > .page-hero, :scope > .about-hero, :scope > .brand-hero')) return;
    collect(section);
  });
  if (!units.length) return;

  const io = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((e) => e.isIntersecting).map((e) => e.target);
      const rank = new Map();
      visible.forEach((el) => {
        const n = rank.get(el.parentElement) || 0;
        rank.set(el.parentElement, n + 1);
        io.unobserve(el);
        el.style.setProperty('--reveal-delay', `${Math.min(n, 5) * 80}ms`);
        el.classList.add('is-revealed');
        const done = () => {
          el.classList.remove('reveal', 'is-revealed');
          el.style.removeProperty('--reveal-delay');
        };
        el.addEventListener('transitionend', (e) => e.propertyName === 'opacity' && done(), { once: true });
        setTimeout(done, 1600 + n * 80);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  document.documentElement.classList.add('js-reveal');
  units.forEach((el) => {
    el.classList.add('reveal');
    io.observe(el);
  });
})();
